import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { BASE_URL, authHeader } from "../integrations/APIClient";

const SESSION_KEY = "chesshood:usage-session:v1";
const SOURCE_KEY = "chesshood:usage-source:v1";
const HEARTBEAT_MS = 30_000;
const ACTIVE_WINDOW_MS = 75_000;

function sessionId() {
  try {
    let value = window.sessionStorage.getItem(SESSION_KEY);
    if (value) return value;
    value = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, value);
    return value;
  } catch {
    return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function initialSource() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(SOURCE_KEY) || "null");
    if (saved) return saved;
    const params = new URLSearchParams(window.location.search);
    const next = {
      referrer: document.referrer || "direct",
      utm: {
        source: params.get("utm_source") || "",
        medium: params.get("utm_medium") || "",
        campaign: params.get("utm_campaign") || "",
      },
    };
    window.sessionStorage.setItem(SOURCE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return { referrer: document.referrer || "direct", utm: {} };
  }
}

function safeActionLabel(target) {
  const element = target?.closest?.("button,a,[role='button']");
  if (!element) return "";
  const raw =
    element.getAttribute("data-analytics") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.textContent ||
    "";
  return String(raw)
    .replace(/\s+/g, " ")
    .replace(/[\r\n\t]/g, " ")
    .trim()
    .slice(0, 80);
}

async function sendUsage(payload, { beacon = false } = {}) {
  const body = JSON.stringify({
    sessionId: sessionId(),
    ...initialSource(),
    ...payload,
  });

  if (beacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(`${BASE_URL}/api/analytics/event`, blob)) return;
    } catch {}
  }

  try {
    const headers = await authHeader(new Headers({ "Content-Type": "application/json" }));
    await fetch(`${BASE_URL}/api/analytics/event`, {
      method: "POST",
      headers,
      body,
      keepalive: true,
    });
  } catch {
    // Analytics is best-effort and never blocks the product.
  }
}

export function trackUsageEvent(event, options = {}) {
  const page = typeof window !== "undefined" ? window.location.pathname : "/";
  return sendUsage({
    event,
    page,
    action: options.action || "",
    activeSeconds: options.activeSeconds || 0,
    meta: options.meta || {},
  });
}

export default function UsageAnalytics() {
  const location = useLocation();
  const { firebaseUser } = useContext(UserContext);
  const lastInteractionRef = useRef(Date.now());
  const lastHeartbeatRef = useRef(Date.now());

  useEffect(() => {
    const markActive = () => { lastInteractionRef.current = Date.now(); };
    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((name) => window.addEventListener(name, markActive, { passive: true }));
    return () => events.forEach((name) => window.removeEventListener(name, markActive));
  }, []);

  useEffect(() => {
    sendUsage({
      event: "page_view",
      page: location.pathname,
      meta: { variant: location.pathname.includes("beta") ? "beta" : "alpha" },
    });
  }, [location.pathname, firebaseUser?.uid]);

  useEffect(() => {
    const clickHandler = (event) => {
      const action = safeActionLabel(event.target);
      if (!action) return;
      sendUsage({
        event: "action",
        page: window.location.pathname,
        action,
      });
    };
    document.addEventListener("click", clickHandler, true);
    return () => document.removeEventListener("click", clickHandler, true);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      const visible = document.visibilityState === "visible";
      const recentlyActive = now - lastInteractionRef.current <= ACTIVE_WINDOW_MS;
      const elapsed = Math.max(0, Math.round((now - lastHeartbeatRef.current) / 1000));
      lastHeartbeatRef.current = now;
      if (!visible || !recentlyActive) return;
      sendUsage({
        event: "heartbeat",
        page: window.location.pathname,
        activeSeconds: Math.min(60, elapsed),
      });
    }, HEARTBEAT_MS);

    const end = () => {
      const now = Date.now();
      const recentlyActive = now - lastInteractionRef.current <= ACTIVE_WINDOW_MS;
      sendUsage({
        event: "session_end",
        page: window.location.pathname,
        activeSeconds: recentlyActive ? Math.min(30, Math.round((now - lastHeartbeatRef.current) / 1000)) : 0,
      }, { beacon: true });
    };
    window.addEventListener("pagehide", end);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", end);
    };
  }, []);

  return null;
}
